'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, MessageSquare } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: 'Online Order Inquiries',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Header */}
      <section className="bg-[#FAFBF9] border-b border-neutral-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100/70 px-3.5 py-1 rounded-full">
            <MessageSquare className="w-3.5 h-3.5 text-emerald-700" />
            <span>Dedicated Customer Care & Trade Desks</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-neutral-900 uppercase tracking-tight">
            GET IN TOUCH WITH US
          </h1>

          <p className="text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto font-medium leading-relaxed">
            Have questions about an online order, catering quantities, or international container exports? 
            Our team is at your service six days a week.
          </p>
        </div>
      </section>

      {/* 2. Contact Information Cards & Form */}
      <section className="py-14 bg-white flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            
            {/* Direct Channels (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <h2 className="text-xl font-bold text-neutral-900 uppercase tracking-tight">
                Headquarters & Contact Details
              </h2>

              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80">
                  <MapPin className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-neutral-900 uppercase">Kitchen & Experience Center</h4>
                    <p className="text-xs text-neutral-600 mt-1 font-medium leading-relaxed">
                      12 South Masi Street, Near Meenakshi Amman Temple,<br />
                      Madurai, Tamil Nadu 625001, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80">
                  <Phone className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-neutral-900 uppercase">Telephone & WhatsApp</h4>
                    <p className="text-xs text-neutral-600 mt-1 font-medium">
                      Customer Care: +91 94432 10890<br />
                      Office Landline: (0452) 234-7890
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80">
                  <Mail className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-neutral-900 uppercase">Email Desks</h4>
                    <p className="text-xs text-neutral-600 mt-1 font-medium">
                      Retail Orders: care@elavarasifoods.com<br />
                      Corporate & Bulk: corporate@elavarasifoods.com<br />
                      International Export: export@elavarasifoods.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80">
                  <Clock className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-neutral-900 uppercase">Operating Hours</h4>
                    <p className="text-xs text-neutral-600 mt-1 font-medium">
                      Monday to Saturday: 8:00 AM – 9:00 PM IST<br />
                      Sunday: 9:00 AM – 2:00 PM (Retail Flagship Only)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Inquiry Form (7 cols) */}
            <div className="lg:col-span-7">
              <div className="bg-[#FAFBF9] border border-neutral-200/90 rounded-3xl p-6 sm:p-10 shadow-sm space-y-6">
                <div className="space-y-1">
                  <h3 className="text-2xl font-black text-neutral-900 uppercase tracking-tight">
                    Send Us a Message
                  </h3>
                  <p className="text-xs text-neutral-500 font-medium">
                    We typically respond to inquiries within 2-3 hours during business hours.
                  </p>
                </div>

                {submitted ? (
                  <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                    <h4 className="text-base font-bold text-emerald-950">Message Sent Successfully!</h4>
                    <p className="text-xs text-emerald-800">
                      Thank you for contacting Elavarasi Foods. Our support specialist has received your note and will be in touch shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Your Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Email Address *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                          className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Department</label>
                        <select
                          value={formData.department}
                          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                          className="w-full px-3 py-2 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                        >
                          <option>Online Order Inquiries</option>
                          <option>Weddings & Family Catering</option>
                          <option>Corporate Gifting</option>
                          <option>B2B Retail & Wholesale</option>
                          <option>Export Trade</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Your Message *</label>
                      <textarea
                        required
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Please write your questions or requirements here..."
                        className="w-full px-3.5 py-2.5 text-xs bg-white border border-neutral-300 rounded-xl focus:outline-none focus:border-emerald-600"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all cursor-pointer flex items-center justify-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
